/**
 * Component: Quantity selector
 * ------------------------------------------------------------------------------
 * A form control component to increment/decrement a counter.
 *
 * @namespace quantitySelector
 */
import {on} from '../helpers/utils';
import bind from '../mixins/bind';

/**
 * DOM selectors.
 */
const selectors = {
  decrement: '[js-quantity-selector="decrement"]',
  increment: '[js-quantity-selector="increment"]',
  input: '[js-quantity-selector="input"]',
};

/**
 * Create a new quantitySelector object.
 * @param {string} selector - Node id selector.
 * @param {Function} callback - Callback function executed after a quantity update.
 */
export default (selector, callback = null) => {

  /**
   * Instance settings.
   */
  const namespace = selector;
  const container = document.getElementById(selector);

  /**
   * Node selectors.
   */
  const nodeSelectors = {
    decrement: container.querySelector(selectors.decrement),
    increment: container.querySelector(selectors.increment),
    input: container.querySelector(selectors.input),
  };

  /**
   * Initialise component bind.
   */
  const binder = bind(container);

  /**
   * Initialise component.
   */
  function init() {
    if (binder.isSet()) {
      return;
    }

    setEventBusListeners();
    setEventListeners();
    binder.set();

    if (container.dataset.available === 'false') {
      nodeSelectors.decrement.setAttribute('disabled', true);
      nodeSelectors.increment.setAttribute('disabled', true);
    }

    if (nodeSelectors.input.value <= 1) {
      nodeSelectors.decrement.setAttribute('disabled', true);
    }

    if (nodeSelectors.input.value === container.getAttribute('data-max')) {
      nodeSelectors.increment.setAttribute('disabled', true);
    }
  }

  /**
   * Set listeners for each instance.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen(`${namespace}:update`, (value) => {
      update(value);
      executeCallback();
    });

    Frame.EventBus.listen(`${namespace}:updateMax`, (value) => {
      updateMax(value);
    });
  }

  /**
   * Set click listeners.
   */
  function setEventListeners() {
    on('click', nodeSelectors.increment, (event) => handleQuantityUpdate('increment', event));
    on('click', nodeSelectors.decrement, (event) => handleQuantityUpdate('decrement', event));
  }

  /**
   * Handle quantity updates.
   * @param {string} type - Quantity update type (increment/decrement).
   * @param {Event} event - Click event.
   */
  function handleQuantityUpdate(type, event) {
    event.preventDefault();
    const inputValue = nodeSelectors.input.value;
    const currentValue = (type === 'increment') ? parseInt(inputValue, 10) + 1 : parseInt(inputValue, 10) - 1;
    handleButtonState(currentValue);

    if (validate(currentValue)) {
      Frame.EventBus.emit(`${namespace}:update`, currentValue);
    }
  }

  /**
   * Validate input.
   * @param {Integer} value - Value to validate.
   */
  function validate(value) {
    return (
      !isNaN(value) &&
      value >= 1 &&
      isBelowMaximum(value)
    );
  }

  /**
   * Check if container value is below maximum inventory limit.
   * @param {Integer} value - Value to validate.
   */
  function isBelowMaximum(value) {
    if (!container.hasAttribute('data-max')) {
      throw new Error('Could not find data-max attribute on container');
    }

    if (getMaxThresholdValue() === 0) {
      return true;
    }

    return (value <= getMaxThresholdValue());
  }

  /**
   * Get maximum threshold value of inventory.
   */
  function getMaxThresholdValue() {
    return Number(container.getAttribute('data-max'));
  }

  /**
   * Update DOM value and execute callback.
   * @param {Integer} value - Value to replace on DOM.
   */
  function update(value) {
    nodeSelectors.input.setAttribute('value', value);
  }

  /**
   * Update maximum limit on quantity selectors.
   * @param {Integer} value - Maximum quantity limit before disabling decrease/increase buttons.
   */
  function updateMax(value) {
    reset();

    if (value === 1) {
      nodeSelectors.increment.setAttribute('disabled', true);
      return;
    }

    if (typeof value !== 'undefined') {
      container.setAttribute('data-max', value);

      if (value <= 0) {
        container.setAttribute('data-max', 999);
      }

      return;
    }

    container.setAttribute('data-max', 0);
  }

  /**
   * Update availability state on quantity selectors.
   * @param {boolean} isAvailable - Variant availability.
   */
  function updateAvailability(isAvailable) {
    container.setAttribute('data-availability', isAvailable);
  }

  /**
   * Reset quantity selector state.
   * Sets initial state of decrement to disabled
   */
  function reset() {

    /**
     * Check if variant is sold out using availability state on container.
     */
    if (container.dataset.availability === 'false') {
      update(0);
      nodeSelectors.increment.setAttribute('disabled', true);
      nodeSelectors.decrement.setAttribute('disabled', true);
      return;
    }

    update(1);
    nodeSelectors.increment.removeAttribute('disabled');
    nodeSelectors.decrement.setAttribute('disabled', true);
  }

  /**
   * Handle maximum threshold state.
   * @param {Integer} value - Predicted value to validate.
   */
  function handleButtonState(currentValue) {

    /**
     * Disables increment button if the current value to more than or equal to the inventory quantity.
     */
    if (currentValue >= getMaxThresholdValue()) {
      nodeSelectors.increment.setAttribute('disabled', true);

      /**
       * If the increment is disabled but the current value is over 1 the decrement button is enabled.
       */
      if (currentValue > 1) {
        nodeSelectors.decrement.removeAttribute('disabled');
        return;
      }
      return;
    }

    nodeSelectors.increment.removeAttribute('disabled');

    /**
     * If the current value is less than or equal to 1 the decrement button is disabled
     */
    if (currentValue <= 1) {
      nodeSelectors.decrement.setAttribute('disabled', true);
      return;
    }

    nodeSelectors.decrement.removeAttribute('disabled');
  }

  /**
   * Execute callback function defined in constructor.
   */
  function executeCallback() {
    return (typeof callback === 'function') ? callback() : false;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    update,
    updateMax,
    updateAvailability,
  });
};
